import type { ReactNode } from 'react';
import { InsforgeBrowserProvider } from '../client/provider';
import type { InsforgeProviderProps, InitialAuthState } from '@insforge/react';
import { cookies } from 'next/headers';
import { COOKIE_NAMES, parseUserCookie } from '../lib/cookies';

export interface InsforgeServerProviderProps extends Omit<InsforgeProviderProps, 'initialState'> {
  children: ReactNode;
  /**
   * The base URL of your InsForge backend.
   *
   * @deprecated Use `InsforgeBrowserProvider` in a client component instead.
   * This prop is kept for backward compatibility only.
   */
  baseUrl?: string;
  /**
   * The anonymous key for your InsForge backend.
   *
   * @deprecated Use `InsforgeBrowserProvider` in a client component instead.
   * This prop is kept for backward compatibility only.
   */
  anonKey?: string;
  /**
   * Opt into dynamic rendering to read auth state from cookies during SSR.
   *
   * - When true: Reads cookies on server, prevents content flashing, opts into dynamic rendering
   * - When false: Static rendering, may see brief content flash during hydration
   *
   * @default true
   */
  dynamic?: boolean;
}

/**
 * @deprecated Use `InsforgeServerProviderProps` instead.
 */
export type InsforgeProviderServerProps = InsforgeServerProviderProps;

/**
 * Get initial auth state from cookies (server-side)
 *
 * This helper reads the authentication state from cookies set by the SDK.
 * Use this in Server Components to get the initial auth state and pass it
 * to `InsforgeBrowserProvider` via the `initialState` prop.
 *
 * This prevents hydration mismatches by ensuring SSR and client render have the same state.
 *
 * @example
 * ```tsx
 * // app/layout.tsx (Server Component)
 * import { getAuthFromCookies } from '@insforge/nextjs';
 * import { Providers } from './providers';
 *
 * export default async function RootLayout({ children }) {
 *   const initialState = await getAuthFromCookies();
 *
 *   return (
 *     <html>
 *       <body>
 *         <Providers initialState={initialState}>
 *           {children}
 *         </Providers>
 *       </body>
 *     </html>
 *   );
 * }
 * ```
 *
 * @returns Initial auth state with user and userId, or null values if not authenticated
 */
export async function getAuthFromCookies(): Promise<InitialAuthState> {
  try {
    const cookieStore = await cookies();

    // Read session token from cookie (set by middleware or /api/auth route)
    const token = cookieStore.get(COOKIE_NAMES.SESSION)?.value || null;

    if (!token) {
      // Not authenticated - return null to indicate signed out
      return {
        user: null,
        userId: null,
      };
    }

    // Read user identity from cookie (set by middleware or /api/auth route)
    const userCookie = cookieStore.get(COOKIE_NAMES.USER)?.value;

    try {
      const user = parseUserCookie(userCookie);

      // Return basic user info from cookie
      // This is lightweight data for SSR hydration only
      // Full profile will be loaded by SDK's getCurrentSession() after hydration
      return {
        user,
        userId: user?.id || null,
      };
    } catch {
      // Invalid user data in cookie
      return {
        user: null,
        userId: null,
      };
    }
  } catch (error) {
    // Error reading cookies (might be in middleware or edge runtime)
    console.error('[Insforge] Error reading cookies:', error);
    return {
      user: null,
      userId: null,
    };
  }
}

/**
 * @deprecated **This component is deprecated.** Use `InsforgeBrowserProvider` instead.
 *
 * This Server Component wrapper doesn't work correctly because SDK client instances
 * (class objects) cannot be serialized and passed from Server to Client Components.
 *
 * ---
 *
 * ## Migration Guide
 *
 * ### Before (deprecated):
 * ```tsx
 * // app/layout.tsx
 * import { InsforgeServerProvider } from '@insforge/nextjs';
 * import { insforge } from '@/lib/insforge';
 *
 * export default function RootLayout({ children }) {
 *   return (
 *     <InsforgeServerProvider client={insforge}> // ❌ This fails!
 *       {children}
 *     </InsforgeServerProvider>
 *   );
 * }
 * ```
 *
 * ### After (recommended):
 *
 * **Step 1:** Create a client-side Providers component:
 * ```tsx
 * // app/providers.tsx
 * 'use client';
 * import { InsforgeBrowserProvider, type InitialAuthState } from '@insforge/nextjs';
 * import { insforge } from '@/lib/insforge';
 *
 * export function Providers({
 *   children,
 *   initialState,
 * }: {
 *   children: React.ReactNode;
 *   initialState?: InitialAuthState;
 * }) {
 *   return (
 *     <InsforgeBrowserProvider client={insforge} initialState={initialState}>
 *       {children}
 *     </InsforgeBrowserProvider>
 *   );
 * }
 * ```
 *
 * **Step 2:** Use it in your layout:
 * ```tsx
 * // app/layout.tsx
 * import { getAuthFromCookies } from '@insforge/nextjs';
 * import { Providers } from './providers';
 *
 * export default async function RootLayout({ children }) {
 *   const initialState = await getAuthFromCookies();
 *
 *   return (
 *     <html>
 *       <body>
 *         <Providers initialState={initialState}>
 *           {children}
 *         </Providers>
 *       </body>
 *     </html>
 *   );
 * }
 * ```
 *
 * ---
 *
 * @see InsforgeBrowserProvider - The recommended client-side provider
 * @see getAuthFromCookies - Helper to get initial auth state for SSR
 */
export async function InsforgeServerProvider(
  props: InsforgeServerProviderProps
): Promise<React.JSX.Element> {
  const { children, dynamic = true, baseUrl, anonKey, ...restProps } = props;

  if (baseUrl || anonKey) {
    console.warn(
      '[Insforge] ⚠️ InsforgeServerProvider is deprecated and will not work correctly.\n' +
        'SDK client instances cannot be passed from Server to Client Components in Next.js.\n\n' +
        'Please migrate to InsforgeBrowserProvider:\n' +
        '1. Create a client component (e.g., app/providers.tsx) with "use client"\n' +
        '2. Use InsforgeBrowserProvider with your SDK client in that component\n' +
        '3. Use getAuthFromCookies() in your layout to pass initialState\n\n' +
        'See: https://insforge.com/docs/nextjs/migration'
    );
  }

  // Only read cookies if dynamic=true (opts into dynamic rendering)
  // When dynamic=false, Next.js can statically generate the page
  const initialAuth = dynamic ? await getAuthFromCookies() : { user: null, userId: null };

  // Note: This will fail at runtime because `client` is a class instance
  // We still attempt to render for backward compatibility, but it won't work
  return (
    <InsforgeBrowserProvider initialState={initialAuth} {...restProps}>
      {children}
    </InsforgeBrowserProvider>
  );
}

/**
 * @deprecated Use `InsforgeServerProvider` instead (which is also deprecated).
 * Migrate to `InsforgeBrowserProvider` for proper Next.js App Router support.
 */
export const InsforgeProvider = InsforgeServerProvider;
